import {
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    Input,
    InputLabel,
    TextField
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Alert } from '@material-ui/lab';
import { useParams } from 'react-router';
import { doSwaggerCall } from '../../../../hooks/useSwaggerClient';
import { useNotify } from '../../../../hooks/useNotify';
import FormSection from '../../../../components/atom/FormSection';
import { DeleteIcon } from '../../../../icons';

const emptyDocument = {
    id: null,
    name: null,
    type: '',
    startDate: null,
    endDate: null,
    file: null
};

export default function OneDocumentEdit({ editedDoc, editFinished, onClose }) {
    const { clientId } = useParams();
    const { notifyError, notifySuccess, notifyInfo } = useNotify();
    const [doc, setDoc] = useState(editedDoc || emptyDocument);
    const [error, setError] = useState(null);

    const createDocument = async () => {
        if (doc.name === '' || doc.type === '' || !doc.file || !doc.startDate || !doc.endDate) {
            setError(`Please fill out all fields and upload a document!`);
            return;
        }
        setError(null);
        notifyInfo(`Upload started for ${doc.type}`);
        await doSwaggerCall('documents', 'uploadDocument', {
            document: doc.file
        }).then(async name => {
            await doSwaggerCall('clients', 'createDocument', {
                clientId,
                newDocument: {
                    name,
                    type: doc.type,
                    startDate: moment(doc.startDate).format('YYYY-MM-DD'),
                    endDate: moment(doc.endDate).format('YYYY-MM-DD')
                }
            })
                .then(res => {
                    editFinished(res);
                    onClose();
                })
                .catch(err => {
                    notifyError(err.response.body.message);
                });
            notifySuccess(`Upload finished for ${doc.type}`);
        });
    };

    const editDocument = () => {
        doSwaggerCall('documents', 'updateDocument', {
            documentId: doc.id,
            updatedDocument: {
                type: doc.type,
                startDate: moment(doc.startDate).format('YYYY-MM-DD'),
                endDate: moment(doc.endDate).format('YYYY-MM-DD'),
                archive: doc.archive
            }
        })
            .then(resDoc => {
                editFinished(resDoc);
                notifySuccess('Document successfully modified!');
            })
            .catch(err => {
                notifyError(err.message);
            });
    };

    // update on prop change
    useEffect(() => {
        if (editedDoc) {
            setDoc({
                ...editedDoc,
                archive: !!editedDoc.archivedAt
            });
        } else {
            setDoc(emptyDocument);
        }
    }, [editedDoc]);

    const uploadDocument = event => {
        if (!event.target.files || !event.target.files.length) {
            return;
        }
        setDoc({ ...doc, file: event.target.files[0] });
    };

    return (
        <>
            {error && (
                <Grid item xs={12} className="mb-2 mt-2">
                    <Alert severity="error">{error}</Alert>
                </Grid>
            )}
            <FormSection title="File settings">
                <Grid container item xs={12} spacing={2}>
                    <Grid item xs={12}>
                        {doc.name ? (
                            <>
                                <InputLabel style={{ marginBottom: '3px' }} shrink>
                                    File:
                                </InputLabel>
                                <a href={doc.name} target="_blank" rel="noreferrer">
                                    <Button variant="contained" component="span" size="small">
                                        Download
                                    </Button>
                                </a>
                            </>
                        ) : (
                            <>
                                <InputLabel
                                    style={{ marginBottom: '3px' }}
                                    shrink
                                    htmlFor="contained-button-file"
                                >
                                    File:
                                </InputLabel>
                                <label htmlFor="contained-button-file">
                                    <Input
                                        id="contained-button-file"
                                        type="file"
                                        style={{ display: 'none' }}
                                        onChange={ev => uploadDocument(ev)}
                                    />
                                    {!doc.file && (
                                        <Button variant="contained" component="span" size="small">
                                            Upload
                                        </Button>
                                    )}
                                </label>
                                {doc.file && <span>{doc.file.name}</span>}
                                {doc.file && (
                                    <IconButton
                                        size="small"
                                        variant="contained"
                                        component="span"
                                        onClick={() => {
                                            setDoc({ ...doc, file: null });
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            name="type"
                            label="Document type"
                            value={doc.type}
                            onChange={ev => setDoc({ ...doc, type: ev.target.value })}
                        />
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <DatePicker
                            label="Start date"
                            value={doc.startDate}
                            onChange={newDate => {
                                if (
                                    doc.startDate !== null &&
                                    moment(newDate).isAfter(moment(doc.endDate))
                                ) {
                                    return;
                                }
                                setDoc({ ...doc, startDate: newDate });
                            }}
                            disableMaskedInput
                            renderInput={props => <TextField {...props} fullWidth helperText="" />}
                        />
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <DatePicker
                            label="End date"
                            value={doc.endDate}
                            onChange={newDate => {
                                if (
                                    doc.endDate !== null &&
                                    moment(newDate).isBefore(moment(doc.startDate))
                                ) {
                                    return;
                                }
                                setDoc({ ...doc, endDate: newDate });
                            }}
                            disableMaskedInput
                            renderInput={props => <TextField {...props} fullWidth helperText="" />}
                        />
                    </Grid>
                    {/* archive option */}
                    {editedDoc.id && (
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={doc.archive || false}
                                        onChange={ev => {
                                            setDoc({
                                                ...doc,
                                                archive: ev.target.checked
                                            });
                                        }}
                                    />
                                }
                                label="Archive document"
                            />
                            {doc.archivedAt && (
                                <div style={{ marginLeft: 32, fontSize: 12, color: '#666' }}>
                                    {`Archived at: ${moment
                                        .utc(doc.archivedAt)
                                        .format('MMM DD, YYYY')}`}
                                </div>
                            )}
                        </Grid>
                    )}
                </Grid>
            </FormSection>
            <Grid item xs={6}>
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => (editedDoc.id ? editDocument() : createDocument())}
                >
                    {editedDoc.id ? 'Edit document' : 'Save document'}
                </Button>
            </Grid>
            <Grid item xs={6}>
                <Button fullWidth variant="contained" onClick={onClose}>
                    Back
                </Button>
            </Grid>
        </>
    );
}
